import React, { useState, useEffect, useRef, useCallback, useContext } from 'react'
import { Navigate, useParams } from 'react-router-dom'
import { HistoryRouteContext } from 'src/routes'
import getUserById from 'src/services/get/getUserById'
import getUserMaterialItemPositions from 'src/services/get/getUserMaterialItemPositions'
import postUserLike from 'src/services/post/postUserLike'
import { HouseGrid3D } from 'src/pages/main/house/HouseGrid3D'
import UserProfileCard from 'src/components/house/UserProfileCard'
import HouseCommentDisplay from 'src/components/house/HouseCommentDisplay'
import BtnBack from 'src/assets/img/house/btn_back 1.svg'
import LikeOn from 'src/assets/img/house/like_on.svg'
import LikeOff from 'src/assets/img/house/like_off.svg'
import BG from 'src/assets/img/house/BG.png'
import { Wrapper } from 'src/components/common/Wrapper'
import { MainLayout } from 'src/components/layouts/MainLayout'
import { usePinchZoom } from 'src/pages/main/house/hooks/usePinchZoom'
import { useAuthState } from 'src/providers/AuthProviders'
import { MAIN_PATHS } from 'src/routes/main'

const getConceptSlots = (item) => {
  if (!item) return []

  if (item.individual_space === 'concept') {
    return ['floor', 'left', 'right']
  }

  if (item.individual_space === 'wall') {
    if (item.wall_position === 'left') return ['left']
    if (item.wall_position === 'right') return ['right']

    const slots = []
    if (item.left_wall_image_url) slots.push('left')
    if (item.right_wall_image_url) slots.push('right')

    return slots.length > 0 ? slots : ['left']
  }

  return ['floor']
}

const getRoomDimensions = (rank) => {
  switch (rank) {
    case 'bronze':
      return { gridSize: 8, wallRows: 5 }
    case 'silver':
      return { gridSize: 10, wallRows: 5 }
    case 'gold':
      return { gridSize: 12, wallRows: 5 }
    default:
      return { gridSize: 8, wallRows: 5 }
  }
}

const buildDroppedItems = (regularItems) => {
  const newDroppedItems = {}

  regularItems.forEach((position) => {
    const rawPlane = position.individual_space || 'floor'
    const wallPosition = position.wall_position
    const plane = rawPlane === 'wall' ? (wallPosition === 'right' ? 'right' : 'left') : rawPlane
    const startX = position.grid_x
    const startY = position.grid_y
    const reverseFlag = !!position.reverse_flag
    const itemWidth = reverseFlag
      ? position.right_x_space || position.x_space || 1
      : position.x_space || 1
    const itemHeight = reverseFlag
      ? position.right_y_space || position.y_space || 1
      : position.y_space || 1
    const itemIndex = position.index || 1
    const positionId = position.id

    const posIconUrl = position.icon_url
    const posLeftUrl = position.left_material_image_url || ''
    const posRightUrl = position.right_material_image_url || ''

    const centerX = startX + Math.floor(itemWidth / 2)
    const centerY = startY + Math.floor(itemHeight / 2)

    for (let dx = 0; dx < itemWidth; dx++) {
      for (let dy = 0; dy < itemHeight; dy++) {
        const key = `${plane}-${startX + dx}-${startY + dy}`
        if (startX + dx === centerX && startY + dy === centerY) {
          const displayImage = reverseFlag && posRightUrl ? posRightUrl : posLeftUrl

          newDroppedItems[key] = {
            id: position.user_character_cloth_id,
            category: position.category,
            individual_space: plane,
            icon_url: posIconUrl,
            startX,
            startY,
            isCenter: true,
            itemWidth,
            itemHeight,
            itemIndex,
            reverse_flag: reverseFlag,
            positionId,
            displayImage,
            left_material_image_url: posLeftUrl,
            right_material_image_url: posRightUrl,
            isSavedInGridArea: true
          }
        } else {
          newDroppedItems[key] = {
            occupied: true,
            startX,
            startY,
            itemWidth,
            itemHeight,
            positionId
          }
        }
      }
    }
  })

  return newDroppedItems
}

export default function UserHousePage() {
  const { userId: paramUserId } = useParams()
  const { user: currentUser } = useAuthState()
  const { redirectTo } = useContext(HistoryRouteContext)
  if (currentUser.id === Number(paramUserId)) {
    return <Navigate to="/mypage" replace />
  }

  const [user, setUser] = useState(null)
  const [isLiked, setIsLiked] = useState(false)
  const [isCommentModalOpen, setIsCommentModalOpen] = useState(false)
  const [droppedItems, setDroppedItems] = useState({})
  const [backgroundImage, setBackgroundImage] = useState('')
  const [floorBackgroundImage, setFloorBackgroundImage] = useState(null)
  const [leftWallBackgroundImage, setLeftWallBackgroundImage] = useState(null)
  const [rightWallBackgroundImage, setRightWallBackgroundImage] = useState(null)

  const containerRef = useRef(null)
  const { isPinching } = usePinchZoom(containerRef)

  const { gridSize, wallRows } = getRoomDimensions(user?.ranking)
  const tileSize = 40
  const size = gridSize * tileSize + 10
  const wallHeight = wallRows * tileSize + 10

  // Fetch user info
  useEffect(() => {
    if (!paramUserId) return
    const fetchUserInfo = async () => {
      const userInfo = await getUserById(paramUserId)
      if (userInfo) {
        setUser(userInfo)
        setIsLiked(userInfo.is_liked_by_current_user || false)
      } else {
        return redirectTo(MAIN_PATHS.MyPage)
      }
    }
    fetchUserInfo()
  }, [paramUserId])

  // Fetch positions
  useEffect(() => {
    if (!paramUserId) return
    const loadPositions = async () => {
      try {
        const response = await getUserMaterialItemPositions(paramUserId)
        if (!response?.success || !response?.items || !Array.isArray(response.items)) return
        if (response.items.length === 0) {
          setDroppedItems({})
          setFloorBackgroundImage(null)
          setLeftWallBackgroundImage(null)
          setRightWallBackgroundImage(null)
          return
        }

        const specialItems = response.items.filter(
          (item) => item.grid_x === null && item.grid_y === null
        )
        const regularItems = response.items.filter(
          (item) => !(item.grid_x === null && item.grid_y === null)
        )

        setDroppedItems(buildDroppedItems(regularItems))
        setFloorBackgroundImage(null)
        setLeftWallBackgroundImage(null)
        setRightWallBackgroundImage(null)

        specialItems.forEach((specialItem) => {
          const bgUrl = specialItem.background_image_url
          const iconUrl = specialItem.icon_url
          if (specialItem.category === 'background') {
            setBackgroundImage(bgUrl || iconUrl)
          } else if (specialItem.category === 'concept') {
            const slots = getConceptSlots(specialItem)

            if (slots.includes('floor')) {
              setFloorBackgroundImage(specialItem.floor_wall_image_url || iconUrl)
            }
            if (slots.includes('left')) {
              setLeftWallBackgroundImage(specialItem.left_wall_image_url || iconUrl)
            }
            if (slots.includes('right')) {
              setRightWallBackgroundImage(specialItem.right_wall_image_url || iconUrl)
            }
          }
        })
      } catch (error) {
        console.error('Error loading saved positions:', error)
      }
    }
    loadPositions()
  }, [paramUserId])

  const handleAvatarClick = () => {
    if (!user) return
    setIsCommentModalOpen((prev) => !prev)
  }

  const handleLikeClick = async () => {
    if (!paramUserId) return

    const previousState = isLiked
    const previousLikeCount = user?.like_count || 0
    const newIsLiked = !previousState

    setIsLiked(newIsLiked)
    setUser((prev) => ({
      ...prev,
      like_count: newIsLiked ? previousLikeCount + 1 : previousLikeCount - 1
    }))

    try {
      const result = await postUserLike(paramUserId)
      if (!result) {
        setIsLiked(previousState)
        setUser((prev) => ({
          ...prev,
          like_count: previousLikeCount
        }))
      }
    } catch (error) {
      console.error('Failed to like user:', error)
      setIsLiked(previousState)
      setUser((prev) => ({
        ...prev,
        like_count: previousLikeCount
      }))
    }
  }

  const noopFn = useCallback(() => {}, [])

  const getItemAtPosition = useCallback(
    (plane, x, y) => {
      const key = `${plane}-${x}-${y}`
      return droppedItems[key] || null
    },
    [droppedItems]
  )

  const getItemsForPlane = useCallback(
    (plane) =>
      Object.values(droppedItems).filter(
        (item) => item.isCenter && item.individual_space === plane
      ),
    [droppedItems]
  )

  const isTileHovered = useCallback(() => false, [])

  return (
    <Wrapper>
      <MainLayout showHeader={false} showFooter={false}>
        <div
          className="flex items-center justify-center relative"
          style={{ height: '100dvh', width: '100%' }}
        >
          <div className="absolute inset-0 z-0">
            <img src={backgroundImage || BG} alt="" className="h-full object-cover opacity-80" />
          </div>
          {user && (
            <div className="absolute flex justify-between items-start top-3 left-3 right-3 z-50 font-ShinMaruGo tracking-wider">
              <button
                onClick={() => redirectTo(-1)}
                className="w-28
                 hover:scale-105 transition-transform"
              >
                <img src={BtnBack} alt="戻る" />
              </button>
              <div className="min-w-[200px] max-w-[85%] flex flex-col items-end left-0 gap-4 cursor-pointer">
                <UserProfileCard
                  user={user}
                  rank={user.ranking}
                  showEditButton={false}
                  onAvatarClick={handleAvatarClick}
                />
                <button
                  onClick={handleLikeClick}
                  className="cursor-pointer hover:scale-105 transition-transform"
                >
                  <img
                    src={isLiked ? LikeOn : LikeOff}
                    alt={isLiked ? 'いいね済み' : 'いいね'}
                    className="h-6 w-auto"
                  />
                </button>
              </div>
            </div>
          )}

          <HouseGrid3D
            containerRef={containerRef}
            isPinching={isPinching}
            gridSize={gridSize}
            wallRows={wallRows}
            tileSize={tileSize}
            size={size}
            wallHeight={wallHeight}
            isLayoutMode={false}
            hoverPosition={null}
            draggingItem={null}
            justDroppedKey={null}
            getItemAtPosition={getItemAtPosition}
            getItemsForPlane={getItemsForPlane}
            isTileHovered={isTileHovered}
            setHoverPosition={noopFn}
            setDraggingItem={noopFn}
            onItemClick={noopFn}
            floorBackgroundImage={floorBackgroundImage}
            leftWallBackgroundImage={leftWallBackgroundImage}
            rightWallBackgroundImage={rightWallBackgroundImage}
          />

          {user && (
            <HouseCommentDisplay
              open={isCommentModalOpen}
              onClose={() => setIsCommentModalOpen(false)}
              comment={user.house_comment}
              userName={user.house_name || user.name}
            />
          )}
        </div>
      </MainLayout>
    </Wrapper>
  )
}
